import { postToHost, shortDate, tierColor } from './shared.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { api } from '../../api.js';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — followups/goals/autopilot/templates/drawers/reminders,
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

export interface UserFollowup {
  id: string;
  message_id: string | null;
  thread_id: string | null;
  subject: string | null;
  from_addr: string | null;
  note: string;
  due_at: string | null;
  status: string;
  created_at: string;
  completed_at: string | null;
  snoozed_until: string | null;
}

export function UserFollowupsSection(): React.JSX.Element {
  const [items, setItems] = useState<UserFollowup[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'done' | 'all'>('pending');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newDue, setNewDue] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editNote, setEditNote] = useState('');
  const [editDue, setEditDue] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.addinUserFollowupsList(statusFilter);
      setItems(r.followups);
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'load failed' });
    } finally {
      setBusy(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  async function add() {
    if (!newNote.trim()) return;
    try {
      await api.addinUserFollowupCreate({ note: newNote.trim(), ...(newDue ? { due_at: newDue } : {}) });
      setNewNote('');
      setNewDue('');
      setAdding(false);
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'add failed' });
    }
  }

  async function saveEdit(id: string) {
    try {
      await api.addinUserFollowupPatch(id, { note: editNote, due_at: editDue || null });
      setEditId(null);
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'save failed' });
    }
  }

  async function toggleDone(f: UserFollowup) {
    await api.addinUserFollowupPatch(f.id, { status: f.status === 'done' ? 'pending' : 'done' });
    load();
  }

  async function remove(id: string) {
    if (!confirm('Delete this followup?')) return;
    await api.addinUserFollowupDelete(id);
    load();
  }

  return (
    <Card padding="md" radius="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between" wrap="wrap">
          <Stack gap={2}>
            <Title order={5}>My followups</Title>
            <Text size="xs" c="dimmed">Things you explicitly want to remember to do. Add, edit, delete, mark done.</Text>
          </Stack>
          <Group gap="xs">
            <SegmentedControl
              size="xs"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as typeof statusFilter)}
              data={[{ value: 'pending', label: 'Pending' }, { value: 'done', label: 'Done' }, { value: 'all', label: 'All' }]}
            />
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)}>
              New
            </Button>
          </Group>
        </Group>

        {adding && (
          <Card withBorder padding="xs" bg="var(--mantine-color-blue-0)">
            <Stack gap="xs">
              <Textarea
                placeholder="What do you need to follow up on?"
                value={newNote}
                onChange={(e) => setNewNote(e.currentTarget.value)}
                minRows={2}
                autoFocus
              />
              <Group gap="xs">
                <TextInput
                  type="datetime-local"
                  value={newDue}
                  onChange={(e) => setNewDue(e.currentTarget.value)}
                  size="xs"
                  style={{ flex: 1 }}
                  placeholder="Due (optional)"
                />
                <Button size="xs" onClick={add} disabled={!newNote.trim()}>Save</Button>
                <Button size="xs" variant="subtle" onClick={() => { setAdding(false); setNewNote(''); setNewDue(''); }}>Cancel</Button>
              </Group>
            </Stack>
          </Card>
        )}

        {busy && !items ? <Loader size="sm" /> : null}
        {items && items.length === 0 && !adding && (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No {statusFilter === 'all' ? '' : statusFilter} followups. Click "New" to add one.
          </Text>
        )}

        {items && items.length > 0 && (
          <Stack gap={4}>
            {items.map((f) => (
              <Card key={f.id} withBorder padding="xs">
                {editId === f.id ? (
                  <Stack gap="xs">
                    <Textarea
                      value={editNote}
                      onChange={(e) => setEditNote(e.currentTarget.value)}
                      minRows={2}
                    />
                    <Group gap="xs">
                      <TextInput
                        type="datetime-local"
                        value={editDue}
                        onChange={(e) => setEditDue(e.currentTarget.value)}
                        size="xs"
                        style={{ flex: 1 }}
                      />
                      <Button size="xs" onClick={() => saveEdit(f.id)}>Save</Button>
                      <Button size="xs" variant="subtle" onClick={() => setEditId(null)}>Cancel</Button>
                    </Group>
                  </Stack>
                ) : (
                  <Group justify="space-between" wrap="nowrap" gap="xs">
                    <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                      <Checkbox
                        checked={f.status === 'done'}
                        onChange={() => toggleDone(f)}
                        size="sm"
                      />
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={500} lineClamp={2} td={f.status === 'done' ? 'line-through' : undefined}>
                          {f.note}
                        </Text>
                        <Group gap={6}>
                          {f.due_at && (
                            <Badge size="xs" color={
                              new Date(f.due_at).getTime() < Date.now() && f.status !== 'done' ? 'red' : 'gray'
                            }>
                              {new Date(f.due_at).toLocaleString()}
                            </Badge>
                          )}
                          {f.subject && (
                            <Text size="xs" c="dimmed" lineClamp={1} maw={250}>
                              ↳ {f.subject}
                            </Text>
                          )}
                        </Group>
                      </Stack>
                    </Group>
                    <Group gap={4}>
                      {f.message_id && (
                        <Tooltip label="Open email in Outlook">
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            onClick={() => postToHost({ type: 'open_in_outlook', message_id: f.message_id! })}
                          >
                            <IconMail size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label="Edit">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          onClick={() => {
                            setEditId(f.id);
                            setEditNote(f.note);
                            setEditDue(f.due_at ?? '');
                          }}
                        >
                          <IconEdit size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete">
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => remove(f.id)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                )}
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

// =========================================================================
// Round 28-P Phase 5.1 — Goals panel.
// =========================================================================

export interface UserGoal {
  id: string; name: string; description: string | null;
  target_date: string | null; status: string;
  created_at: string; updated_at: string; completed_at: string | null;
}

export function GoalsPanel(): React.JSX.Element {
  const [items, setItems] = useState<UserGoal[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<UserGoal | { id: ''; name: string; description: string; target_date: string; status: 'active' } | null>(null);
  const [scoring, setScoring] = useState<UserGoal | null>(null);
  const [scoreResult, setScoreResult] = useState<Awaited<ReturnType<typeof api.addinGoalScore>> | null>(null);
  const [scoreBusy, setScoreBusy] = useState(false);

  async function load() {
    setBusy(true);
    try {
      const r = await api.addinGoalsList();
      setItems(r.goals);
    } finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!editing) return;
    if (!editing.name.trim()) {
      notifications.show({ color: 'red', message: 'Goal name required' });
      return;
    }
    try {
      if (!editing.id) {
        await api.addinGoalCreate({
          name: editing.name,
          ...(editing.description ? { description: editing.description } : {}),
          ...(editing.target_date ? { target_date: editing.target_date } : {}),
        });
      } else {
        await api.addinGoalPatch(editing.id, {
          name: editing.name,
          description: editing.description ?? null,
          target_date: editing.target_date || null,
          status: 'status' in editing ? editing.status : 'active',
        });
      }
      setEditing(null);
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'save failed' });
    }
  }

  async function setStatus(id: string, status: string) {
    await api.addinGoalPatch(id, { status });
    load();
  }

  async function remove(id: string) {
    if (!confirm('Delete this goal?')) return;
    await api.addinGoalDelete(id);
    load();
  }

  async function runScore(g: UserGoal) {
    setScoring(g);
    setScoreResult(null);
    setScoreBusy(true);
    try {
      const r = await api.addinGoalScore(g.id, 20);
      setScoreResult(r);
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'scoring failed' });
    } finally {
      setScoreBusy(false);
    }
  }

  return (
    <Stack gap="sm">
      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between">
          <Stack gap={2}>
            <Title order={5}>Goals</Title>
            <Text size="xs" c="dimmed">
              Declare what you're trying to accomplish, then "Score emails" to find which recent inbox messages advance each goal.
              Works for any objective: closing a deal, resolving a backlog, hiring a role, completing a project.
            </Text>
          </Stack>
          <Button size="xs" leftSection={<IconPlus size={14} />}
            onClick={() => setEditing({ id: '', name: '', description: '', target_date: '', status: 'active' })}>
            New goal
          </Button>
        </Group>
      </Card>

      {busy && !items ? <Loader size="sm" /> : null}
      {items && items.length === 0 && (
        <Card padding="md" withBorder>
          <Text size="sm" c="dimmed" ta="center">
            No goals yet. Add one to start triaging your inbox by what matters most right now.
          </Text>
        </Card>
      )}

      {items?.map((g) => (
        <Card key={g.id} withBorder padding="sm" style={{ borderLeft: g.status === 'active' ? '4px solid var(--mantine-color-blue-5)' : '4px solid var(--mantine-color-gray-3)' }}>
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Group gap={6}>
                <Text fw={500}>{g.name}</Text>
                <Badge size="xs" color={g.status === 'active' ? 'blue' : g.status === 'paused' ? 'yellow' : 'green'} variant="light">
                  {g.status}
                </Badge>
                {g.target_date && (
                  <Badge size="xs" variant="light" color={
                    new Date(g.target_date).getTime() < Date.now() && g.status === 'active' ? 'red' : 'gray'
                  }>
                    due {new Date(g.target_date).toLocaleDateString()}
                  </Badge>
                )}
              </Group>
              {g.description && (
                <Text size="xs" c="dimmed" lineClamp={3} style={{ whiteSpace: 'pre-wrap' }}>{g.description}</Text>
              )}
            </Stack>
            <Group gap={4}>
              <Tooltip label="Score recent emails for relevance to this goal">
                <Button size="compact-xs" variant="light" leftSection={<IconBrain size={12} />}
                  onClick={() => runScore(g)} disabled={g.status !== 'active'}>
                  Score
                </Button>
              </Tooltip>
              <Menu position="bottom-end">
                <Menu.Target>
                  <ActionIcon size="sm" variant="subtle"><IconMoonStars size={14} /></ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => setEditing(g)}>Edit</Menu.Item>
                  {g.status === 'active' && <Menu.Item onClick={() => setStatus(g.id, 'paused')}>Pause</Menu.Item>}
                  {g.status === 'paused' && <Menu.Item onClick={() => setStatus(g.id, 'active')}>Resume</Menu.Item>}
                  {g.status !== 'done' && <Menu.Item onClick={() => setStatus(g.id, 'done')}>Mark done</Menu.Item>}
                  <Menu.Divider />
                  <Menu.Item color="red" onClick={() => remove(g.id)}>Delete</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </Card>
      ))}

      {/* Edit goal modal */}
      <Modal opened={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Edit goal' : 'New goal'} size="md">
        {editing && (
          <Stack gap="sm">
            <TextInput label="Name" placeholder="e.g. Close Acme Q3 deal"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.currentTarget.value })}
              required autoFocus
            />
            <Textarea label="Description (optional)"
              placeholder="Context that helps the AI understand what counts as relevant. e.g. 'Negotiating a $50k contract with Acme Corp; main contact is jane@acme.com'"
              value={editing.description ?? ''}
              onChange={(e) => setEditing({ ...editing, description: e.currentTarget.value })}
              minRows={3}
            />
            <TextInput label="Target date (optional)" type="date"
              value={editing.target_date ?? ''}
              onChange={(e) => setEditing({ ...editing, target_date: e.currentTarget.value })}
            />
            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save}>Save</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Score result modal */}
      <Modal opened={!!scoring} onClose={() => { setScoring(null); setScoreResult(null); }}
        title={scoring ? `Score: ${scoring.name}` : ''} size="xl">
        {scoreBusy && <Center p="md"><Loader /></Center>}
        {scoreResult && (
          <Stack gap="xs">
            {scoreResult.scored.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No recent emails scored ≥4 for this goal. Either nothing relevant came in lately, or the goal description could be more specific.
              </Text>
            ) : (
              <ScrollArea h={500}>
                <Stack gap="xs">
                  {scoreResult.scored.map((s) => (
                    <Card key={s.message_id} withBorder padding="xs">
                      <Group justify="space-between" wrap="nowrap" gap="xs">
                        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                          <Group gap={6}>
                            <Badge size="sm" color={s.score >= 8 ? 'red' : s.score >= 6 ? 'orange' : 'blue'}>
                              {s.score}/10
                            </Badge>
                            <Text fw={500} size="sm" lineClamp={1}>{s.subject ?? '(no subject)'}</Text>
                          </Group>
                          <Text size="xs" c="dimmed">{s.from_addr} · {new Date(s.received_at).toLocaleDateString()}</Text>
                          <Text size="xs" fs="italic" c="dimmed">{s.why}</Text>
                        </Stack>
                        <ActionIcon size="sm" variant="subtle"
                          onClick={() => postToHost({ type: 'open_in_outlook', message_id: s.message_id })}>
                          <IconMail size={14} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </ScrollArea>
            )}
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

// =========================================================================
// Round 28-P Phase 5.2 — Autopilot draft queue.
// =========================================================================

export interface AutopilotDraft {
  id: string; message_id: string; subject: string | null; from_addr: string | null;
  draft_body: string; tone: string | null; confidence: number | null;
  generated_at: string; status: string;
  reviewed_at: string | null; sent_at: string | null;
  edited_body: string | null; cost_usd: number;
}

export function AutopilotPanel(): React.JSX.Element {
  const [drafts, setDrafts] = useState<AutopilotDraft[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [genTone, setGenTone] = useState<'professional' | 'casual' | 'concise' | 'detailed'>('professional');
  const [genLimit, setGenLimit] = useState(5);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');

  async function load() {
    setBusy(true);
    try {
      const r = await api.addinAutopilotList();
      setDrafts(r.drafts);
    } finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  async function generate() {
    setGenBusy(true);
    try {
      const r = await api.addinAutopilotGenerate({ limit: genLimit, tone: genTone });
      notifications.show({
        color: r.drafts_generated > 0 ? 'green' : 'blue',
        message: r.message ?? `Generated ${r.drafts_generated} draft${r.drafts_generated === 1 ? '' : 's'} (${typeof r.total_cost_usd === 'number' ? '$' + r.total_cost_usd.toFixed(4) : '$0'})`,
      });
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'generation failed' });
    } finally { setGenBusy(false); }
  }

  async function setStatus(id: string, status: string) {
    await api.addinAutopilotPatch(id, { status });
    load();
  }

  async function saveEdit(id: string) {
    await api.addinAutopilotPatch(id, { edited_body: editingBody, status: 'edited' });
    setEditingId(null);
    setEditingBody('');
    load();
  }

  async function remove(id: string) {
    if (!confirm('Discard this draft?')) return;
    await api.addinAutopilotDelete(id);
    load();
  }

  function send(d: AutopilotDraft) {
    const body = d.edited_body ?? d.draft_body;
    postToHost({
      type: 'send_as_reply',
      message_id: d.message_id,
      subject: d.subject ?? '',
      body,
      to: d.from_addr ?? '',
    });
    setStatus(d.id, 'sent');
    notifications.show({ color: 'green', message: 'Draft sent to Outlook for review' });
  }

  return (
    <Stack gap="sm">
      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between" wrap="wrap">
          <Stack gap={2}>
            <Group gap="xs">
              <Title order={5}>Autopilot drafts</Title>
              <Badge size="xs" variant="light" color="orange">manual trigger</Badge>
            </Group>
            <Text size="xs" c="dimmed">
              AI batch-drafts replies to your highest-priority recent emails (P0/P1/P2 from last 7 days).
              Review, edit, approve. Nothing auto-sends — you stay in control.
            </Text>
          </Stack>
          <Group gap="xs">
            <SegmentedControl
              size="xs"
              value={genTone}
              onChange={(v) => setGenTone(v as typeof genTone)}
              data={[
                { value: 'professional', label: 'Pro' },
                { value: 'casual', label: 'Casual' },
                { value: 'concise', label: 'Concise' },
                { value: 'detailed', label: 'Detailed' },
              ]}
            />
            <NumberInput
              size="xs"
              w={70}
              min={1} max={20}
              value={genLimit}
              onChange={(v) => setGenLimit(typeof v === 'number' ? v : 5)}
            />
            <Button size="xs" leftSection={<IconBrain size={14} />} onClick={generate} loading={genBusy}>
              Generate batch
            </Button>
          </Group>
        </Group>
      </Card>

      {busy && !drafts ? <Loader size="sm" /> : null}
      {drafts && drafts.length === 0 && (
        <Card padding="md" withBorder>
          <Text size="sm" c="dimmed" ta="center">
            No draft queue. Click "Generate batch" to have the AI draft replies to your top {genLimit} priority emails.
          </Text>
        </Card>
      )}

      {drafts?.map((d) => (
        <Card key={d.id} withBorder padding="sm" style={{
          borderLeft: d.status === 'approved' ? '4px solid var(--mantine-color-green-5)' : '4px solid var(--mantine-color-blue-5)',
        }}>
          <Stack gap="xs">
            <Group justify="space-between" wrap="nowrap" gap="xs">
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Group gap={6}>
                  <Text fw={500} size="sm" lineClamp={1}>{d.subject ?? '(no subject)'}</Text>
                  {d.tone && <Badge size="xs" variant="light" color="blue">{d.tone}</Badge>}
                  <Badge size="xs" variant="light" color={
                    d.status === 'pending' ? 'gray' :
                    d.status === 'approved' ? 'green' :
                    d.status === 'edited' ? 'orange' :
                    d.status === 'sent' ? 'teal' : 'red'
                  }>{d.status}</Badge>
                </Group>
                <Text size="xs" c="dimmed">
                  {d.from_addr} · drafted {new Date(d.generated_at).toLocaleString()}
                  {typeof d.cost_usd === 'number' && d.cost_usd > 0 && ` · $${d.cost_usd.toFixed(4)}`}
                </Text>
              </Stack>
              <Group gap={4}>
                <Tooltip label="Open original email">
                  <ActionIcon size="sm" variant="subtle"
                    onClick={() => postToHost({ type: 'open_in_outlook', message_id: d.message_id })}>
                    <IconMail size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>

            {editingId === d.id ? (
              <Stack gap="xs">
                <Textarea
                  value={editingBody}
                  onChange={(e) => setEditingBody(e.currentTarget.value)}
                  minRows={6} autosize
                />
                <Group gap="xs" justify="flex-end">
                  <Button size="compact-xs" variant="subtle" onClick={() => setEditingId(null)}>Cancel</Button>
                  <Button size="compact-xs" onClick={() => saveEdit(d.id)}>Save edit</Button>
                </Group>
              </Stack>
            ) : (
              <Box bg="var(--mantine-color-gray-0)" p="xs" style={{ borderRadius: 4 }}>
                <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                  {d.edited_body ?? d.draft_body}
                </Text>
              </Box>
            )}

            {editingId !== d.id && (
              <Group gap="xs" justify="flex-end">
                <Button size="compact-xs" variant="subtle" leftSection={<IconEdit size={12} />}
                  onClick={() => { setEditingId(d.id); setEditingBody(d.edited_body ?? d.draft_body); }}>
                  Edit
                </Button>
                <Button size="compact-xs" variant="subtle" color="red" leftSection={<IconX size={12} />}
                  onClick={() => remove(d.id)}>
                  Reject
                </Button>
                {d.status !== 'sent' && (
                  <Button size="compact-xs" leftSection={<IconSend size={12} />} onClick={() => send(d)}>
                    Send to Outlook
                  </Button>
                )}
              </Group>
            )}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

// =========================================================================
// Round 28-N Phase 3.3 — reply templates panel.
// =========================================================================

export interface ReplyTemplate {
  id: string; name: string; body: string; tone: string | null;
  tags: string[]; use_count: number; last_used_at: string | null;
  created_at: string; updated_at: string;
}

export function ReplyTemplatesPanel(): React.JSX.Element {
  const [items, setItems] = useState<ReplyTemplate[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<ReplyTemplate | { id: ''; name: ''; body: ''; tone: null; tags: []; use_count: 0; last_used_at: null; created_at: ''; updated_at: '' } | null>(null);

  async function load() {
    setBusy(true);
    try {
      const r = await api.addinReplyTemplatesList();
      setItems(r.templates);
    } finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!editing) return;
    if (!editing.name.trim() || !editing.body.trim()) {
      notifications.show({ color: 'red', message: 'Name and body required' });
      return;
    }
    try {
      if (!editing.id) {
        await api.addinReplyTemplateCreate({
          name: editing.name, body: editing.body,
          ...(editing.tone != null ? { tone: editing.tone } : {}), tags: editing.tags,
        });
      } else {
        await api.addinReplyTemplatePatch(editing.id, {
          name: editing.name, body: editing.body,
          ...(editing.tone != null ? { tone: editing.tone } : {}), tags: editing.tags,
        });
      }
      setEditing(null);
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'save failed' });
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this template?')) return;
    await api.addinReplyTemplateDelete(id);
    load();
  }

  return (
    <Stack gap="sm">
      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between">
          <Stack gap={2}>
            <Title order={5}>Reply templates</Title>
            <Text size="xs" c="dimmed">
              Saved snippets you can drop into AI drafts. Use {`{{name}}`}, {`{{subject}}`} placeholders for substitution.
            </Text>
          </Stack>
          <Button size="xs" leftSection={<IconPlus size={14} />}
            onClick={() => setEditing({ id: '', name: '', body: '', tone: null, tags: [], use_count: 0, last_used_at: null, created_at: '', updated_at: '' })}>
            New template
          </Button>
        </Group>
      </Card>

      {busy && !items ? <Loader size="sm" /> : null}
      {items && items.length === 0 && (
        <Card padding="md" withBorder>
          <Text size="sm" c="dimmed" ta="center">
            No templates yet. Create one for replies you send often (e.g. "Decline a meeting", "Payment overdue followup").
          </Text>
        </Card>
      )}

      {items && items.map((t) => (
        <Card key={t.id} withBorder padding="sm">
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Group gap={6}>
                <Text fw={500} size="sm">{t.name}</Text>
                {t.tone && <Badge size="xs" variant="light" color="blue">{t.tone}</Badge>}
                {t.tags.map((tag) => (
                  <Badge key={tag} size="xs" variant="light" color="gray">{tag}</Badge>
                ))}
                {t.use_count > 0 && (
                  <Badge size="xs" variant="light" color="green">used {t.use_count}×</Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed" lineClamp={3} style={{ whiteSpace: 'pre-wrap' }}>{t.body}</Text>
            </Stack>
            <Group gap={4}>
              <Tooltip label="Edit">
                <ActionIcon size="sm" variant="subtle" onClick={() => setEditing(t)}>
                  <IconEdit size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Delete">
                <ActionIcon size="sm" variant="subtle" color="red" onClick={() => remove(t.id)}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        </Card>
      ))}

      <Modal opened={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Edit template' : 'New template'} size="lg">
        {editing && (
          <Stack gap="sm">
            <TextInput label="Name" placeholder="e.g. Decline a meeting"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.currentTarget.value })}
              required
            />
            <Textarea label="Body" placeholder="Hi {{name}}, thanks for reaching out about {{subject}}. ..."
              value={editing.body}
              onChange={(e) => setEditing({ ...editing, body: e.currentTarget.value })}
              minRows={6}
              autosize
              required
            />
            <Select label="Tone (optional)"
              data={[
                { value: '', label: '— none —' },
                { value: 'professional', label: 'Professional' },
                { value: 'casual', label: 'Casual' },
                { value: 'concise', label: 'Concise' },
                { value: 'detailed', label: 'Detailed' },
              ]}
              value={editing.tone ?? ''}
              onChange={(v) => setEditing({ ...editing, tone: v || null })}
              clearable={false}
            />
            <TextInput label="Tags (comma-separated)"
              placeholder="sales, followup"
              value={editing.tags.join(', ')}
              onChange={(e) => setEditing({ ...editing, tags: e.currentTarget.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            />
            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save}>Save</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

// =========================================================================
// Round 28-M Phase 2.1 — Ask AI floating drawer (chat with email context).
// =========================================================================

export function AskAiDrawer({
  message_id, subject, onClose,
}: {
  message_id: string;
  subject?: string;
  onClose: () => void;
}): React.JSX.Element {
  const [history, setHistory] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [insights, setInsights] = useState<Awaited<ReturnType<typeof api.addinAiInsights>> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Load AI insights on mount — runs in parallel with the user's first question.
  useEffect(() => {
    api.addinAiInsights(message_id).then(setInsights).catch(() => { /* silent */ });
  }, [message_id]);

  // Auto-scroll to latest message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history.length]);

  async function send() {
    const q = input.trim();
    if (!q || busy) return;
    const next = [...history, { role: 'user' as const, content: q }];
    setHistory(next);
    setInput('');
    setBusy(true);
    try {
      const r = await api.addinAskAi({ message_id, messages: next });
      setHistory([...next, { role: 'assistant', content: r.reply }]);
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof Error ? e.message : 'AI call failed',
      });
    } finally {
      setBusy(false);
    }
  }

  // Suggested questions to seed the chat.
  const suggestions = [
    'What is this email asking?',
    'Draft a reply',
    'How urgent is this?',
    'What action should I take?',
    'Summarize the key points',
  ];

  return (
    <Drawer
      opened
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Stack gap={2}>
          <Group gap="xs">
            <IconMessageQuestion size={18} />
            <Text fw={600}>Ask AI</Text>
          </Group>
          {subject && <Text size="xs" c="dimmed" lineClamp={1} maw={400}>{subject}</Text>}
        </Stack>
      }
    >
      <Stack gap="xs" h="calc(100vh - 120px)">
        {/* Insights pill row */}
        {insights && (
          <Card withBorder padding="xs" bg="var(--mantine-color-gray-0)">
            <Stack gap={4}>
              {insights.ai_analysis.suggested_action && (
                <Group gap="xs" wrap="nowrap">
                  <Badge size="xs" color="blue">suggested</Badge>
                  <Text size="xs">{insights.ai_analysis.suggested_action}</Text>
                </Group>
              )}
              <Group gap="xs" wrap="wrap">
                {insights.ai_analysis.tone && (
                  <Badge size="xs" variant="light" color="grape">{insights.ai_analysis.tone}</Badge>
                )}
                {insights.ai_analysis.intent && (
                  <Badge size="xs" variant="light" color="teal">{insights.ai_analysis.intent}</Badge>
                )}
                {insights.sender_stats.prior_count > 0 && (
                  <Badge size="xs" variant="light" color="gray">
                    {insights.sender_stats.prior_count} prior emails from this sender
                  </Badge>
                )}
              </Group>
              {insights.ai_analysis.key_points && insights.ai_analysis.key_points.length > 0 && (
                <Stack gap={2} mt={4}>
                  {insights.ai_analysis.key_points.map((kp) => (
                    <Text key={kp} size="xs" c="dimmed">• {kp}</Text>
                  ))}
                </Stack>
              )}
            </Stack>
          </Card>
        )}

        {/* Chat history */}
        <Box ref={scrollRef} style={{ flex: 1, overflowY: 'auto' }}>
          {history.length === 0 && (
            <Stack gap="xs" align="center" py="md">
              <Text size="sm" c="dimmed">Ask me anything about this email.</Text>
              <Group gap={4} wrap="wrap" justify="center">
                {suggestions.map((s) => (
                  <Button key={s} size="compact-xs" variant="light" onClick={() => setInput(s)}>
                    {s}
                  </Button>
                ))}
              </Group>
            </Stack>
          )}
          <Stack gap="xs">
            {history.map((m, i) => (
              <Card
                key={i}
                withBorder
                padding="xs"
                {...(m.role === 'user' ? { bg: 'var(--mantine-color-blue-0)' } : {})}
                ml={m.role === 'user' ? 'auto' : 0}
                mr={m.role === 'user' ? 0 : 'auto'}
                maw="92%"
              >
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{m.content}</Text>
              </Card>
            ))}
            {busy && (
              <Card withBorder padding="xs" maw="92%">
                <Group gap="xs"><Loader size="xs" /><Text size="sm" c="dimmed">Thinking…</Text></Group>
              </Card>
            )}
          </Stack>
        </Box>

        {/* Input */}
        <Group gap="xs" wrap="nowrap" align="flex-end">
          <Textarea
            placeholder="Ask anything about this email…"
            value={input}
            onChange={(e) => setInput(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
            minRows={2}
            maxRows={6}
            style={{ flex: 1 }}
            disabled={busy}
            autoFocus
          />
          <Button onClick={send} disabled={!input.trim() || busy} loading={busy}>
            Send
          </Button>
        </Group>
        <Text size="xs" c="dimmed" ta="right">⌘/Ctrl+Enter to send</Text>
      </Stack>
    </Drawer>
  );
}

// =========================================================================
// Round 28-M Phase 2.4 — Draft reply drawer with tone presets.
// =========================================================================

export function DraftReplyDrawer({
  message_id, subject, onClose,
}: {
  message_id: string;
  subject?: string;
  onClose: () => void;
}): React.JSX.Element {
  const [tone, setTone] = useState<'professional' | 'casual' | 'concise' | 'detailed'>('professional');
  const [instructions, setInstructions] = useState('');
  const [draft, setDraft] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [cost, setCost] = useState<number>(0);
  // Round 28-N Phase 3.3: template picker.
  const [templates, setTemplates] = useState<ReplyTemplate[] | null>(null);
  useEffect(() => {
    api.addinReplyTemplatesList().then((r) => setTemplates(r.templates)).catch(() => { /* silent */ });
  }, []);
  function applyTemplate(t: ReplyTemplate) {
    // Substitute placeholders.
    let body = t.body;
    body = body.replace(/\{\{subject\}\}/g, subject ?? '');
    body = body.replace(/\{\{name\}\}/g, '[name]');
    setDraft(body);
    if (t.tone) setTone(t.tone as typeof tone);
    // Bump use_count async.
    api.addinReplyTemplatePatch(t.id, { bump_use: true }).catch(() => { /* silent */ });
    notifications.show({ color: 'blue', message: `Applied template: ${t.name}` });
  }

  async function generate() {
    setBusy(true);
    try {
      const r = await api.addinDraftReply({ message_id, tone, ...(instructions ? { instructions } : {}) });
      setDraft(r.draft);
      setCost(r.cost_usd);
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Draft failed' });
    } finally {
      setBusy(false);
    }
  }

  // Auto-generate the first draft on mount.
  useEffect(() => { generate(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  function copy() {
    navigator.clipboard.writeText(draft).then(() =>
      notifications.show({ color: 'green', message: 'Draft copied to clipboard' }),
    );
  }

  function sendToOutlook() {
    postToHost({ type: 'send_as_reply', message_id, subject: subject ?? '', body: draft, to: '' });
    notifications.show({ message: 'Reply sent to Outlook for review' });
  }

  return (
    <Drawer
      opened
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Stack gap={2}>
          <Group gap="xs">
            <IconPencil size={18} />
            <Text fw={600}>AI draft reply</Text>
          </Group>
          {subject && <Text size="xs" c="dimmed" lineClamp={1} maw={400}>↳ {subject}</Text>}
        </Stack>
      }
    >
      <Stack gap="sm">
        <Box>
          <Text size="xs" c="dimmed" mb={4}>Tone</Text>
          <SegmentedControl
            value={tone}
            onChange={(v) => setTone(v as typeof tone)}
            data={[
              { value: 'professional', label: 'Professional' },
              { value: 'casual', label: 'Casual' },
              { value: 'concise', label: 'Concise' },
              { value: 'detailed', label: 'Detailed' },
            ]}
            fullWidth
          />
        </Box>
        <TextInput
          placeholder="Optional: extra instructions (e.g. 'mention the deadline of Friday')"
          value={instructions}
          onChange={(e) => setInstructions(e.currentTarget.value)}
        />
        <Group gap="xs" wrap="wrap">
          <Button onClick={generate} loading={busy} leftSection={<IconReload size={14} />}>
            {draft ? 'Regenerate' : 'Generate'}
          </Button>
          {templates && templates.length > 0 && (
            <Menu position="bottom-start">
              <Menu.Target>
                <Button variant="default" leftSection={<IconFileText size={14} />}>
                  Use template…
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Saved templates ({templates.length})</Menu.Label>
                {templates.slice(0, 12).map((t) => (
                  <Menu.Item key={t.id} onClick={() => applyTemplate(t)}>
                    {t.name}
                    {t.use_count > 0 && <Text component="span" size="xs" c="dimmed"> · {t.use_count}×</Text>}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}
          {cost > 0 && <Badge size="sm" variant="light" color="gray">${cost.toFixed(4)}</Badge>}
        </Group>
        <Textarea
          minRows={12}
          maxRows={24}
          autosize
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          placeholder={busy ? 'Generating…' : 'Draft will appear here.'}
          disabled={busy}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={onClose}>Close</Button>
          <Button variant="default" onClick={copy} disabled={!draft}>Copy</Button>
          <Button onClick={sendToOutlook} disabled={!draft} leftSection={<IconSend size={14} />}>
            Send to Outlook
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

// =========================================================================
// Round 28-L Phase 1.3b — reminders panel.
// =========================================================================

export function ReminderPanel(): React.JSX.Element {
  const [items, setItems] = useState<Awaited<ReturnType<typeof api.addinRemindersList>>['reminders'] | null>(null);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'fired' | 'all'>('pending');
  const [adding, setAdding] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newRemind, setNewRemind] = useState('');
  const [newChannel, setNewChannel] = useState<'inapp' | 'sms' | 'whatsapp'>('inapp');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.addinRemindersList(statusFilter);
      setItems(r.reminders);
    } finally {
      setBusy(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  async function add() {
    if (!newNote.trim() || !newRemind) return;
    try {
      await api.addinReminderCreate({
        note: newNote.trim(),
        remind_at: new Date(newRemind).toISOString(),
        channel: newChannel,
      });
      setNewNote('');
      setNewRemind('');
      setNewChannel('inapp');
      setAdding(false);
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'add failed' });
    }
  }

  async function dismiss(id: string) {
    await api.addinReminderPatch(id, { status: 'dismissed' });
    load();
  }

  async function remove(id: string) {
    if (!confirm('Delete this reminder?')) return;
    await api.addinReminderDelete(id);
    load();
  }

  // Quick reminder buttons
  function quickAdd(hours: number, label: string) {
    const at = new Date(Date.now() + hours * 3_600_000).toISOString();
    api.addinReminderCreate({ note: `Reminder set ${label}`, remind_at: at }).then(() => load());
  }

  return (
    <Stack gap="sm">
      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between" wrap="wrap">
          <Stack gap={2}>
            <Title order={5}>Reminders</Title>
            <Text size="xs" c="dimmed">
              Set a reminder for any time in the future. Fires as an in-app badge (and SMS/WhatsApp once those channels are wired).
            </Text>
          </Stack>
          <Group gap="xs">
            <SegmentedControl
              size="xs"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as typeof statusFilter)}
              data={[{ value: 'pending', label: 'Pending' }, { value: 'fired', label: 'Fired' }, { value: 'all', label: 'All' }]}
            />
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)}>
              New
            </Button>
          </Group>
        </Group>
      </Card>

      <Card padding="md" radius="md" withBorder>
        <Group gap="xs" mb="xs">
          <Text size="xs" c="dimmed">Quick:</Text>
          <Button size="compact-xs" variant="light" onClick={() => quickAdd(1, 'in 1h')}>+1h</Button>
          <Button size="compact-xs" variant="light" onClick={() => quickAdd(4, 'in 4h')}>+4h</Button>
          <Button size="compact-xs" variant="light" onClick={() => quickAdd(24, 'tomorrow')}>Tomorrow</Button>
          <Button size="compact-xs" variant="light" onClick={() => quickAdd(168, 'next week')}>Next week</Button>
        </Group>

        {adding && (
          <Card withBorder padding="xs" bg="var(--mantine-color-blue-0)" mb="xs">
            <Stack gap="xs">
              <Textarea
                placeholder="What should we remind you about?"
                value={newNote}
                onChange={(e) => setNewNote(e.currentTarget.value)}
                minRows={2}
                autoFocus
              />
              <Group gap="xs" align="center" wrap="nowrap">
                <TextInput
                  type="datetime-local"
                  value={newRemind}
                  onChange={(e) => setNewRemind(e.currentTarget.value)}
                  size="xs"
                  style={{ flex: 1 }}
                  required
                />
                <Tooltip
                  label={
                    newChannel === 'inapp'
                      ? 'Shows as a badge in the addin sidebar'
                      : newChannel === 'sms'
                        ? 'Sends SMS to your verified phone via BroadNet'
                        : 'Sends WhatsApp message to your verified phone via BroadNet'
                  }
                >
                  <SegmentedControl
                    size="xs"
                    value={newChannel}
                    onChange={(v) => setNewChannel(v as 'inapp' | 'sms' | 'whatsapp')}
                    data={[
                      { value: 'inapp', label: 'In-app' },
                      { value: 'sms', label: 'SMS' },
                      { value: 'whatsapp', label: 'WhatsApp' },
                    ]}
                  />
                </Tooltip>
              </Group>
              <Group gap="xs" justify="flex-end">
                <Button size="xs" variant="subtle" onClick={() => { setAdding(false); setNewNote(''); setNewRemind(''); setNewChannel('inapp'); }}>Cancel</Button>
                <Button size="xs" onClick={add} disabled={!newNote.trim() || !newRemind}>
                  {newChannel === 'inapp' ? 'Save' : `Save (${newChannel})`}
                </Button>
              </Group>
              {newChannel !== 'inapp' && (
                <Text size="xs" c="dimmed">
                  ⚠️ {newChannel === 'sms' ? 'SMS' : 'WhatsApp'} requires a verified phone on your user profile and BroadNet {newChannel} enabled in Settings → BroadNet.
                </Text>
              )}
            </Stack>
          </Card>
        )}

        {busy && !items ? <Loader size="sm" /> : null}
        {items && items.length === 0 && !adding && (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No {statusFilter === 'all' ? '' : statusFilter} reminders.
          </Text>
        )}

        {items && items.length > 0 && (
          <Stack gap={4}>
            {items.map((r) => {
              const at = new Date(r.remind_at);
              const isOverdue = r.status === 'pending' && at.getTime() < Date.now();
              const isFired = r.status === 'fired';
              return (
                <Card key={r.id} withBorder padding="xs"
                  style={{ borderColor: isFired ? 'var(--mantine-color-orange-4)' : isOverdue ? 'var(--mantine-color-red-4)' : undefined }}>
                  <Group justify="space-between" wrap="nowrap" gap="xs">
                    <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                      <IconBell size={16} color={isFired ? 'orange' : isOverdue ? 'red' : 'gray'} />
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={500} lineClamp={2}>{r.note ?? r.subject ?? '(no note)'}</Text>
                        <Group gap={6}>
                          <Badge size="xs" color={isFired ? 'orange' : isOverdue ? 'red' : 'blue'}>
                            {isFired ? 'fired' : at.toLocaleString()}
                          </Badge>
                          <Badge size="xs" variant="light" color="gray">{r.channel}</Badge>
                        </Group>
                      </Stack>
                    </Group>
                    <Group gap={4}>
                      {r.message_id && (
                        <Tooltip label="Open email">
                          <ActionIcon size="sm" variant="subtle"
                            onClick={() => postToHost({ type: 'open_in_outlook', message_id: r.message_id! })}>
                            <IconMail size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {r.status === 'fired' && (
                        <Tooltip label="Dismiss">
                          <ActionIcon size="sm" variant="subtle" onClick={() => dismiss(r.id)}>
                            <IconCheck size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label="Delete">
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => remove(r.id)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                </Card>
              );
            })}
          </Stack>
        )}
      </Card>
    </Stack>
  );
}

// =========================================================================
// Follow-ups — threads where I sent the last message and got no reply.
// =========================================================================

export function FollowupsPanel(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinFollowups>> | null>(null);
  const [days, setDays] = useState(3);
  const [busy, setBusy] = useState(false);

  async function load(d: number = days) {
    setBusy(true);
    try {
      setData(await api.addinFollowups(d));
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof Error ? e.message : 'Could not load follow-ups',
      });
    } finally {
      setBusy(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load on mount only
  useEffect(() => {
    load();
  }, []);

  return (
    <Stack gap="sm">
      {/* Round 28-L Phase 1.2b: user-tracked followups (CRUD) above auto-detected list */}
      <UserFollowupsSection />

      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between" wrap="wrap">
          <Stack gap={2}>
            <Title order={5}>Awaiting reply</Title>
            <Text size="sm" c="dimmed">
              Threads where you sent the last message and nobody has replied for{' '}
              <b>{days}+ days</b>. Detected by walking your indexed Sent folder + matching threads
              against later inbound replies.
            </Text>
          </Stack>
          <Group gap="xs">
            <SegmentedControl
              size="xs"
              value={String(days)}
              onChange={(v) => {
                const n = Number(v);
                setDays(n);
                load(n);
              }}
              data={[
                { value: '1', label: '24 h' },
                { value: '3', label: '3 d' },
                { value: '7', label: '1 wk' },
                { value: '14', label: '2 wk' },
                { value: '30', label: '1 mo' },
              ]}
            />
            <Button size="xs" variant="default" onClick={() => load(days)} loading={busy}>
              Refresh
            </Button>
          </Group>
        </Group>
      </Card>

      <Card padding="md" radius="md" withBorder>
        {busy && !data ? (
          <Center p="md">
            <Loader />
          </Center>
        ) : !data || data.followups.length === 0 ? (
          <Center p="xl">
            <Stack align="center" gap={4}>
              <Text fw={500}>Inbox zero ✓</Text>
              <Text size="sm" c="dimmed" ta="center">
                Nothing waiting on a reply more than {days} day{days === 1 ? '' : 's'} old.
              </Text>
              <Text size="xs" c="dimmed" ta="center">
                Tip: index your Sent folder via Index tab → Index ALL mailboxes so this list stays
                accurate.
              </Text>
            </Stack>
          </Center>
        ) : (
          <Stack gap="xs">
            {data.followups.map((f) => {
              const ago = Math.round((Date.now() - new Date(f.received_at).getTime()) / 86400000);
              return (
                <Group
                  key={f.id}
                  gap="sm"
                  wrap="nowrap"
                  onClick={() =>
                    postToHost({ type: 'open_in_outlook', message_id: f.message_id })
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <Badge color={ago >= 7 ? 'red' : ago >= 3 ? 'orange' : 'yellow'}>
                    {ago}d
                  </Badge>
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={500} size="sm" lineClamp={1}>
                      {f.subject || '(no subject)'}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      Sent · last reply: none · {f.from_addr} · {shortDate(f.received_at)}
                    </Text>
                  </Stack>
                  {f.classification_tier && (
                    <Badge color={tierColor(f.classification_tier)} size="xs">
                      {f.classification_tier}
                    </Badge>
                  )}
                </Group>
              );
            })}
          </Stack>
        )}
      </Card>
    </Stack>
  );
}

// =========================================================================
// Index — live progress, auto-index toggle, per-folder stats, event log.
// =========================================================================

// Round 47Q: per-folder coverage. Compares "indexed in brain" vs "items
// shown in Outlook" for each folder, flagging the ones with a gap. Lets the
// user spot Cached Exchange Mode mailboxes (where Outlook shows N items but
// COM only sees M < N because the rest are server-only) without digging.
